import { useHookstate } from "@hookstate/core";
import { useUniversalFindAll } from "../../hooks/universal";
import InputField from "../input-field/InputField";
import { onInputChangeNative, showSnackbar, updateLocalStateRaw } from "../../misc/utils";
import DefaultButton from "../default-button/DefaultButton";
import Plus from "../../assets/icons/Plus";
import Modal from "../Modal/Modal";
import ArrowLeft from "../../assets/icons/ArrowLeft";
import Cross from "../../assets/icons/Cross";
import Tick from "../../assets/icons/Tick";
import { simpleGetter } from "../../actions/common";
import { categoriesApi } from "../../api/api";
import Edit from "../../assets/icons/Edit";
import Delete from "../../assets/icons/Delete";

interface CategoriesProps {
    model: string;
    clubType?: any;
}

export const Categories = ({ model, clubType }: CategoriesProps) => {
    const localState = useHookstate({});
    const state: Record<string, any> = localState.get();
    const search = state.search || "";
    const categoryName = state.categoryName || "";
    const currentCategory = state.currentCategory || {};
    const addModalOpen = Boolean(state.addModalOpen);
    const categoriesTriggerFetch = state.categoriesTriggerFetch || 0;
    const allCategories: any[] = useUniversalFindAll(model, categoriesTriggerFetch);
    const categories = allCategories.filter(c => {
        let retValue = c.name?.toLowerCase().includes(search.toLowerCase())
        if (clubType?.id) {
            retValue = retValue && c.club_type_id === clubType.id
        }
        return retValue
    })

    const toggleModal = () => {
        updateLocalStateRaw(localState, 'addModalOpen')(!addModalOpen)
    }

    const openModal = (mode: string, current?: any) => () => {
        if (mode === "add") {
            updateLocalStateRaw(localState, "currentCategory")(null);
            updateLocalStateRaw(localState, "categoryName")("");
        } else {
            updateLocalStateRaw(localState, "currentCategory")(current);
            updateLocalStateRaw(localState, "categoryName")(current.name);
        }
        toggleModal()
    }

    const submit = async () => {
        const body = {
            model,
            create: {
                name: categoryName
            }
        }
        if (currentCategory.id) {
            //@ts-ignore
            body.create.id = currentCategory.id
        }
        if (clubType?.id) {
            //@ts-ignore
            body.create.club_type_id = clubType?.id
        }
        const response = await simpleGetter(categoriesApi.upsert, body)
        showSnackbar(response.success ? 'Успешно' : 'Произошла ошибка', response.success ? 'success' : 'error')
        if (response.success) {
            updateLocalStateRaw(localState, "categoriesTriggerFetch")(categoriesTriggerFetch + 1)
            toggleModal()
        }
    }

    const handleDelete = (category: any) => async () => {
        const confirmed = window.confirm(`Удалить категорию "${category?.name}"?`)
        if (!confirmed) return;
        const body: any = { model, id: category.id };
        const response = await simpleGetter(categoriesApi.delete, body);
        showSnackbar(response.success ? 'Удалено' : 'Произошла ошибка', response.success ? 'success' : 'error');
        if (response.success) {
            updateLocalStateRaw(localState, "categoriesTriggerFetch")(categoriesTriggerFetch + 1);
        }
    }

    return (
        <div className="flex flex-col flex-1 gap-4 bg-white rounded-xl p-6">
            <div className="flex flex-row items-center">
                <InputField
                    type="text"
                    placeholder="Поиск"
                    value={search}
                    handleChange={onInputChangeNative(localState, 'search')}
                    wrapperClassName="flex-1"
                    additionalClassName="rounded-lg py-3 px-4 bg-valley-background-blue"
                />
                <div className="flex flex-row flex-[3] justify-end items-center">
                    <DefaultButton
                        Icon={Plus}
                        iconStyle="text-valley-dark-green"
                        additionalStyle="text-valley-dark-green border-valley-dark-green py-2 px-4"
                        buttonText="Создать категорию"
                        onClick={openModal('add')}
                    />
                </div>
            </div>
            <div className="flex flex-col overflow-auto">
                <div className="flex flex-row pt-2 pb-4 px-2 border-b border-b-valley-background-blue">
                    <span className="font-medium text-sm leading-[15.4px] text-valley-gray">
                        Название категории
                    </span>
                </div>
                <div className="flex flex-col flex-1 overflow-auto">
                    {categories.map(c => (
                        <div key={c.id} className="flex flex-row items-center py-3 px-2 border-b border-b-valley-background-blue">
                            <div className="flex flex-row flex-1">
                                <span className="font-medium text-[15px] leading-[19.5px] text-valley-black">
                                    {c.name}
                                </span>
                            </div>
                            <div className="flex flex-row justify-end gap-4">
                                <DefaultButton
                                    Icon={Edit}
                                    buttonText="Редактировать"
                                    additionalStyle="px-4 py-[6px] border border-valley-light-gray text-valley-gray items-center gap-2"
                                    onClick={openModal("edit", c)}
                                />
                                <DefaultButton
                                    Icon={Delete}
                                    buttonText="Удалить"
                                    additionalStyle="px-4 py-[6px] border border-valley-red text-valley-red items-center gap-2"
                                    onClick={handleDelete(c)}
                                />
                            </div>
                        </div>
                    ))}
                </div>
            </div>
            <Modal
                visible={addModalOpen}
                setVisible={updateLocalStateRaw(localState, 'addModalOpen')}
                additionalClassName="p-8 w-[32.5%]"
            >
                <div className="flex flex-col gap-8">
                    <span className="text-2xl leading-[26.4px] text-valley-black">
                        Категория
                    </span>
                    <InputField
                        type="textarea"
                        id="category-name"
                        label="Название категории"
                        handleChange={onInputChangeNative(localState, "categoryName")}
                        value={categoryName}
                        additionalClassName="bg-valley-background-blue py-3 px-4 rounded-lg"
                    />
                    <div className="flex-1 flex flex-row gap-3">
                        <DefaultButton
                            buttonText="Вернуться"
                            additionalStyle="flex-1 bg-[#5F6B88] text-white px-8 py-3 text-nowrap"
                            Icon={ArrowLeft}
                            onClick={toggleModal}
                        />
                        <DefaultButton
                            buttonText="Сохранить"
                            additionalStyle="flex-1 bg-valley-dark-green text-white px-8 py-3 text-nowrap"
                            Icon={Tick}
                            onClick={submit}
                            disabled={!categoryName.length}
                        />
                    </div>
                </div>
            </Modal>
        </div>
    )
}